import { useLayoutEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";

import type { FC } from "react";
import type { SxProps, Theme } from "@mui/material/styles";

interface OverflowChipRowProps {
  labels: string[];
  chipSx: SxProps<Theme>;
  maxRows?: number;
  // For "+N more". In a grid cell pass the cell's tabIndex: the grid moves
  // focus to a cell's tabIndex=0 child, which opens the tooltip.
  tabIndex?: number;
}

// Chips that wrap, up to `maxRows` rows; whatever doesn't fit is folded into a
// "+N more" chip whose tooltip lists it. How many fit depends on the cell's
// width, so the chips are measured after layout and re-fitted on resize.
const OverflowChipRow: FC<OverflowChipRowProps> = ({
  labels,
  chipSx,
  maxRows = 2,
  tabIndex = 0,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width))
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={boxRef} sx={{ width: "100%", minWidth: 0 }}>
      {/* Remounting restarts the fit from every chip shown. */}
      <FittedChips
        key={`${width}|${labels.join("|")}`}
        labels={labels}
        chipSx={chipSx}
        maxRows={maxRows}
        tabIndex={tabIndex}
      />
    </Box>
  );
};

const FittedChips: FC<Required<OverflowChipRowProps>> = ({
  labels,
  chipSx,
  maxRows,
  tabIndex,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(labels.length);

  // Drop one chip at a time until the chips, "+N more" included, fit.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || shown === 0) return;
    const rowTops = new Set(
      Array.from(el.children).map((chip) => (chip as HTMLElement).offsetTop)
    );
    if (rowTops.size > maxRows) setShown(shown - 1);
  }, [shown, maxRows]);

  const hidden = labels.slice(shown);

  return (
    <Box ref={ref} sx={{ display: "flex", flexWrap: "wrap", gap: "0.3125rem" }}>
      {labels.slice(0, shown).map((label, index) => (
        <Box key={`${index}-${label}`} sx={chipSx}>
          {label}
        </Box>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {hidden.map((label, index) => (
                <span key={`${index}-${label}`}>{label}</span>
              ))}
            </Box>
          }
        >
          <Box
            tabIndex={tabIndex}
            aria-label={`${hidden.length} more: ${hidden.join(", ")}`}
            sx={chipSx}
          >{`+${hidden.length} more`}</Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default OverflowChipRow;
